import * as uuidv4 from "uuid/v4";

import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { DEFAULT_FONT_SIZE, SLIDE_HEIGHT, SLIDE_WIDTH } from "../constants";
import {
    getCurrentTemplate,
    getPaneState,
    getPresentationId,
    getSelectorDragging,
    getSketchMode,
    getSlides,
    getUndoAction,
    getZoomLevel,
    SlideData,
    State,
    XSelector,
    YSelector,
} from "../reducers";
import * as store from "../services/presentation-store";
import {
    addPoint,
    BoundsType,
    convertPointsToPath,
    createPath,
    createShape,
    Point,
    reducePoints,
    resizePoints,
    ShapeData
} from "../services/shape-service";
import { Undoable } from "./undo";
import { showTextShapeDialog, ShowTextShapeDialogAction } from "./text-shape-dialog";
import { fitTextBox, textNoWrap } from "../services/text-service";
import { wrapNotes } from "../services/slide_helper";
import { reportError, ReportErrorAction } from "./ui-state";


export interface CreateSlideAction extends Undoable {
    readonly type: 'CREATE_SLIDE',
    readonly uuid: string,
    readonly slideX: number,
    readonly slideY: number,
}

export interface UpdateSlideNotesAction extends Undoable {
    readonly type: 'UPDATE_SLIDE_NOTES',
    readonly uuid: string,
    readonly oldText: string,
    readonly newText: string,
    readonly oldLines: ReadonlyArray<string>,
    readonly newLines: ReadonlyArray<string>,
}

export interface StartSlideDraggingAction {
    readonly type: 'START_SLIDE_DRAGGING',
    readonly uuid: string,
    readonly offsetX: number,
    readonly offsetY: number,
    readonly slideX: number,
    readonly slideY: number,
}

export interface SlideDraggingAction extends Undoable {
    readonly type: 'SLIDE_DRAGGING',
    readonly uuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface StopSlideDraggingAction extends Undoable {
    readonly type: 'STOP_SLIDE_DRAGGING',
    readonly uuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface MoveSlidesAction extends Undoable {
    readonly type: 'MOVE_SLIDE',
    readonly uuid: string,
    readonly startX: number,
    readonly startY: number,
    readonly endX: number,
    readonly endY: number,
}

export interface DeleteSlideAction extends Undoable {
    readonly type: 'DELETE_SLIDE',
    readonly slide: SlideData,
}

export interface StartShapeSketchingAction extends Undoable {
    readonly type: 'START_SHAPE_SKETCHING',
    readonly slideUuid: string,
    readonly shape: ShapeData,
    readonly slideX: number,
    readonly slideY: number,
}

export interface ShapeSketchingAction extends Undoable {
    readonly type: 'SHAPE_SKETCHING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly x1: number,
    readonly y1: number,
    readonly x2: number,
    readonly y2: number,
    readonly points: ReadonlyArray<Point>,
    readonly textLines: ReadonlyArray<string>,
    readonly fontSize: number,
    readonly path: string,
}

export interface StopShapeSketchingAction extends Undoable {
    readonly type: 'STOP_SHAPE_SKETCHING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly x1: number,
    readonly y1: number,
    readonly x2: number,
    readonly y2: number,
    readonly points: ReadonlyArray<Point>,
    readonly textLines: ReadonlyArray<string>,
    readonly fontSize: number,
    readonly path: string,
}

export interface CreateShapeAction extends Undoable {
    readonly type: 'CREATE_SHAPE',
    readonly slideUuid: string,
    readonly points: ReadonlyArray<Point>,
    readonly shape: ShapeData,
}

export interface DeleteShapeAction extends Undoable {
    readonly type: 'DELETE_SHAPE',
    readonly slideUuid: string,
    readonly shape: ShapeData,
}

export interface UpdateTextShapeAction extends Undoable {
    readonly type: 'UPDATE_TEXT_SHAPE',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly oldText: string,
    readonly newText: string,
    readonly oldLines: ReadonlyArray<string>,
    readonly newLines: ReadonlyArray<string>,
    readonly oldX1: number,
    readonly oldY1: number,
    readonly oldX2: number,
    readonly oldY2: number,
    readonly newX1: number,
    readonly newY1: number,
    readonly newX2: number,
    readonly newY2: number,
    readonly oldSize: number,
    readonly newSize: number,
}

export interface SelectShapeAction {
    readonly type: 'SELECT_SHAPE',
    readonly shapeUuid: string | null,
}

export interface StartShapeDraggingAction {
    readonly type: 'START_SHAPE_DRAGGING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly offsetX: number,
    readonly offsetY: number,
    readonly shapeX: number,
    readonly shapeY: number,
    readonly boundsOffsetX: number,
    readonly boundsOffsetY: number,
}

export interface ShapeDraggingAction extends Undoable {
    readonly type: 'SHAPE_DRAGGING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface StopShapeDraggingAction extends Undoable {
    readonly type: 'STOP_SHAPE_DRAGGING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly endX: number,
    readonly endY: number,
}

export interface MoveShapeAction extends Undoable {
    readonly type: 'MOVE_SHAPE',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly startX: number,
    readonly startY: number,
    readonly endX: number,
    readonly endY: number,
}

export interface StartSelectorDraggingAction {
    readonly type: 'START_SELECTOR_DRAGGING',
    readonly slideUuid: string,
    readonly shape: ShapeData,
    readonly xSelector: XSelector,
    readonly ySelector: YSelector,
    readonly offsetX: number,
    readonly offsetY: number,
}

export interface SelectorDraggingAction extends Undoable {
    readonly type: 'SELECTOR_DRAGGING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly endX1: number,
    readonly endY1: number,
    readonly endX2: number,
    readonly endY2: number,
    readonly endPoints: ReadonlyArray<Point>,
    readonly endPath: string,
    readonly endLines: ReadonlyArray<string>,
    readonly endSize: number,
}

export interface StopSelectorDraggingAction extends Undoable {
    readonly type: 'STOP_SELECTOR_DRAGGING',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly endX1: number,
    readonly endY1: number,
    readonly endX2: number,
    readonly endY2: number,
    readonly endPoints: ReadonlyArray<Point>,
    readonly endPath: string,
    readonly endLines: ReadonlyArray<string>,
    readonly endSize: number,
}

export interface ResizeShapeAction extends Undoable {
    readonly type: 'RESIZE_SHAPE',
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly startX1: number,
    readonly startY1: number,
    readonly startX2: number,
    readonly startY2: number,
    readonly endX1: number,
    readonly endY1: number,
    readonly endX2: number,
    readonly endY2: number,
    readonly startPoints: ReadonlyArray<Point>,
    readonly endPoints: ReadonlyArray<Point>,
    readonly startPath: string,
    readonly endPath: string,
    readonly startBoundsType: BoundsType,
    readonly startLines: ReadonlyArray<string>,
    readonly startSize: number,
    readonly endLines: ReadonlyArray<string>,
    readonly endSize: number,
}


export function createSlide(): ThunkAction<any, State, any, CreateSlideAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, CreateSlideAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        const { storyboardWidth: width, storyboardHeight: height } = getCurrentTemplate(state);
        const { scrollLeft, scrollTop, paneWidth, paneHeight } = getPaneState(state);
        const canvasRatio = width / height;
        const zoomLevel = getZoomLevel(state);

        const gapLeft = Math.max(0, zoomLevel * (paneWidth - canvasRatio * paneHeight) / 2);
        const posX = scrollLeft - gapLeft + paneWidth / 2;
        const pixelWidth = zoomLevel * paneWidth - 2 * gapLeft;
        const rawNoteX = width * posX / pixelWidth - SLIDE_WIDTH / 2;
        const slideX = Math.max(-SLIDE_WIDTH / 2, Math.min(rawNoteX, width - SLIDE_WIDTH / 2));

        const gapTop = Math.max(0, zoomLevel * (paneHeight - paneWidth / canvasRatio) / 2);
        const posY = scrollTop - gapTop + paneHeight / 2;
        const pixelHeight = zoomLevel * paneHeight - 2 * gapTop;
        const rawNoteY = height * posY / pixelHeight - SLIDE_HEIGHT / 2;
        const slideY = Math.max(-SLIDE_HEIGHT / 2, Math.min(rawNoteY, height - SLIDE_HEIGHT / 2));

        const newUuid = uuidv4();

        const redoEffect = async () => await store.createSlide(presentationId, newUuid, slideX, slideY)
            .catch(error => dispatch(reportError(error)));
        const undoEffect = async () => await store.deleteSlide(presentationId, newUuid)
            .catch(error => dispatch(reportError(error)));

        dispatch({
            type: 'CREATE_SLIDE',
            uuid: newUuid,
            slideX,
            slideY,
            redoEffect,
            undoEffect,
        });

        await redoEffect();
    }
}

export function updateSlideNotes(uuid: string, text: string): ThunkAction<any, State, any, UpdateSlideNotesAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, UpdateSlideNotesAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === uuid);
        if (slide) {
            const presentationId = getPresentationId(state);
            const newLines = wrapNotes(text);
            const redoEffect = async () => await store.updateSlideNotes(presentationId, uuid, text)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.updateSlideNotes(presentationId, uuid, slide.notes)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'UPDATE_SLIDE_NOTES',
                uuid,
                oldText: slide.notes,
                newText: text,
                oldLines: slide.notesLines,
                newLines,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    }
}

export function startSlideDragging(uuid: string, mouseX: number, mouseY: number): ThunkAction<any, State, any, StartSlideDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StartSlideDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === uuid);
        if (slide) {
            dispatch({
                type: 'START_SLIDE_DRAGGING',
                uuid,
                offsetX: slide.x - mouseX,
                offsetY: slide.y - mouseY,
                slideX: slide.x,
                slideY: slide.y,
            })
        }
    };
}

export function slideDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, SlideDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SlideDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'MOVE_SLIDE') {
            const presentationId = getPresentationId(state);
            const {uuid, startX, startY} = previousAction;
            const redoEffect = async () => await store.moveSlide(presentationId, uuid, mouseX, mouseY)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.moveSlide(presentationId, uuid, startX, startY)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'SLIDE_DRAGGING',
                uuid,
                endX: mouseX,
                endY: mouseY,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    }
}


export function stopSlideDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopSlideDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, StopSlideDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'MOVE_SLIDE') {
            const presentationId = getPresentationId(state);
            const {uuid, startX, startY} = previousAction;
            const redoEffect = async () => await store.moveSlide(presentationId, uuid, mouseX, mouseY)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.moveSlide(presentationId, uuid, startX, startY)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'STOP_SLIDE_DRAGGING',
                uuid,
                endX: mouseX,
                endY: mouseY,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    }
}


export function deleteSlide(uuid: string): ThunkAction<any, State, any, DeleteSlideAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, DeleteSlideAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const presentationId = getPresentationId(state);
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === uuid);
        if (slide) {
            const redoEffect = async () => await store.deleteSlide(presentationId, uuid)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.createSlide(presentationId, uuid, slide.x, slide.y)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'DELETE_SLIDE',
                slide,
                undoEffect,
                redoEffect,
            });
            await redoEffect();
        }
    }
}


export function startShapeSketching(slideUuid: string, mouseX: number, mouseY: number): ThunkAction<any, State, any, SelectShapeAction | StartShapeSketchingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SelectShapeAction | StartShapeSketchingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === slideUuid);
        if (slide) {
            const sketchMode = getSketchMode(state);
            if (sketchMode === 'NONE') {
                dispatch(selectShape(null));
            } else {
                const presentationId = getPresentationId(state);
                const shape = createShape(sketchMode, mouseX - slide.x, mouseY - slide.y);
                let redoEffect;
                switch (shape.type) {
                    case 'FREEFORM':
                        redoEffect = async () => await store.createFreeform(presentationId, slideUuid, shape.uuid, mouseX, mouseY, mouseX, mouseY, [{x: 0, y: 0}])
                            .catch(error => dispatch(reportError(error)));
                        break;
                    case 'TEXT':
                        redoEffect = async () => await store.createText(presentationId, slideUuid, shape.uuid, mouseX, mouseY, mouseX, mouseY, '', BoundsType.DYNAMIC)
                            .catch(error => dispatch(reportError(error)));
                        break;
                    default:
                        redoEffect = async () => await store.createShape(presentationId, slideUuid, shape.uuid, shape.type, mouseX, mouseY, mouseX, mouseY)
                            .catch(error => dispatch(reportError(error)));
                        break;
                }
                const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, shape.uuid)
                    .catch(error => dispatch(reportError(error)));
                dispatch({
                    type: 'START_SHAPE_SKETCHING',
                    slideUuid,
                    shape,
                    slideX: slide.x,
                    slideY: slide.y,
                    redoEffect,
                    undoEffect,
                });
                await redoEffect();
            }
        }
    };
}

export function shapeSketching(mouseX: number, mouseY: number): ThunkAction<any, State, any, ShapeSketchingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, ShapeSketchingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'CREATE_SHAPE') {
            const presentationId = getPresentationId(state);
            const {slideUuid, shape} = previousAction;
            switch (shape.type) {
                case 'FREEFORM': {
                    const { uuid } = shape;
                    const { points, x1, y1, x2, y2 } = addPoint(shape, mouseX, mouseY);
                    const path = convertPointsToPath(points);
                    const redoEffect = async () => await store.createFreeform(presentationId, slideUuid, uuid, x1, y1, x2, y2, points)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: uuid,
                        x1,
                        y1,
                        x2,
                        y2,
                        points,
                        textLines: [],
                        fontSize: 0,
                        path,
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateFreeform(presentationId, slideUuid, uuid, x1, y1, x2, y2, points)
                        .catch(error => dispatch(reportError(error)));
                    break;
                }
                case 'TEXT': {
                    const { uuid, x1, y1, text } = shape;
                    const { lines: textLines, size: fontSize} = fitTextBox(shape.text, Math.abs(mouseX - x1), Math.abs(mouseY - y1));
                    const redoEffect = async () => await store.createText(presentationId, slideUuid, uuid, x1, y1, mouseX, mouseY, text, BoundsType.FIXED)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: uuid,
                        x1,
                        y1,
                        x2: mouseX,
                        y2: mouseY,
                        points: [],
                        textLines,
                        fontSize,
                        path: '',
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateText(presentationId, slideUuid, uuid, x1, y1, mouseX, mouseY, text, BoundsType.FIXED)
                        .catch(error => dispatch(reportError(error)));
                    break;
                }
                default: {
                    const { uuid, type, x1, y1 } = shape;
                    const path = createPath(shape, x1, y1, mouseX, mouseY);
                    const redoEffect = async () => await store.createShape(presentationId, slideUuid, uuid, type, x1, y1, mouseX, mouseY)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: uuid,
                        x1,
                        y1,
                        x2: mouseX,
                        y2: mouseY,
                        points: [],
                        textLines: [],
                        fontSize: 0,
                        path,
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateShape(presentationId, slideUuid, uuid, type, x1, y1, mouseX, mouseY)
                        .catch(error => dispatch(reportError(error)));
                    break;
                }
            }
        }
    };
}

export function stopShapeSketching(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopShapeSketchingAction | ShowTextShapeDialogAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, StopShapeSketchingAction | ShowTextShapeDialogAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'CREATE_SHAPE') {
            const presentationId = getPresentationId(state);
            const {slideUuid, shape} = previousAction;
            switch (shape.type) {
                case 'FREEFORM': {
                    const { uuid } = shape;
                    const { points, x1, y1, x2, y2 } = addPoint(shape, mouseX, mouseY);
                    const simplifiedPoints = reducePoints(points);
                    const path = convertPointsToPath(simplifiedPoints);
                    const redoEffect = async () => await store.createFreeform(presentationId, slideUuid, uuid, x1, y1, x2, y2, simplifiedPoints)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'STOP_SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: shape.uuid,
                        x1,
                        y1,
                        x2,
                        y2,
                        points: simplifiedPoints,
                        textLines: [],
                        fontSize: 0,
                        path,
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateFreeform(presentationId, slideUuid, uuid, x1, y1, x2, y2, points)
                        .catch(error => dispatch(reportError(error)));
                    break;
                }
                case 'TEXT': {
                    const { uuid, x1, y1, text, boundsType } = shape;
                    const { lines: textLines, size: fontSize} = fitTextBox(shape.text, Math.abs(mouseX - x1), Math.abs(mouseY - y1));
                    const redoEffect = async () => await store.createText(presentationId, slideUuid, uuid, x1, y1, mouseX, mouseY, text, boundsType)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'STOP_SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: shape.uuid,
                        x1,
                        y1,
                        x2: mouseX,
                        y2: mouseY,
                        points: [],
                        textLines,
                        fontSize,
                        path: '',
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateText(presentationId, slideUuid, uuid, x1, y1, mouseX, mouseY, text, boundsType)
                        .catch(error => dispatch(reportError(error)));
                    if (shape.type === 'TEXT') {
                        dispatch(showTextShapeDialog(slideUuid, shape.uuid, ''));
                    }
                    break;
                }
                default: {
                    const { uuid, type, x1, y1 } = shape;
                    const path = createPath(shape, x1, y1, mouseX, mouseY);
                    const redoEffect = async () => await store.createShape(presentationId, slideUuid, uuid, type, x1, y1, mouseX, mouseY)
                        .catch(error => dispatch(reportError(error)));
                    const undoEffect = async () => await store.deleteShape(presentationId, slideUuid, uuid)
                        .catch(error => dispatch(reportError(error)));
                    dispatch({
                        type: 'STOP_SHAPE_SKETCHING',
                        slideUuid,
                        shapeUuid: shape.uuid,
                        x1,
                        y1,
                        x2: mouseX,
                        y2: mouseY,
                        points: [],
                        textLines: [],
                        fontSize: 0,
                        path,
                        redoEffect,
                        undoEffect,
                    });
                    await store.updateShape(presentationId, slideUuid, uuid, type, x1, y1, mouseX, mouseY)
                        .catch(error => dispatch(reportError(error)));
                    break;
                }
            }
        }
    };
}

export function deleteShape(slideUuid: string, shapeUuid: string): ThunkAction<any, State, any, DeleteShapeAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, DeleteShapeAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === slideUuid);
        if (slide) {
            const shape = slide.shapes.find(currentShape => currentShape.uuid === shapeUuid);
            if (shape) {
                const presentationId = getPresentationId(state);
                const redoEffect = async () => await store.deleteShape(presentationId, slideUuid, shapeUuid)
                    .catch(error => dispatch(reportError(error)));
                let undoEffect;
                switch (shape.type) {
                    case 'FREEFORM':
                        undoEffect = async () => await store.createFreeform(presentationId, slideUuid, shapeUuid, shape.x1, shape.y1, shape.x2, shape.y2, shape.points)
                            .catch(error => dispatch(reportError(error)));
                        break;
                    case 'TEXT':
                        undoEffect = async () => await store.createText(presentationId, slideUuid, shapeUuid, shape.x1, shape.y1, shape.x2, shape.y2, shape.text, shape.boundsType)
                            .catch(error => dispatch(reportError(error)));
                        break;
                    default:
                        undoEffect = async () => await store.createShape(presentationId, slideUuid, shapeUuid, shape.type, shape.x1, shape.y1, shape.x2, shape.y2)
                            .catch(error => dispatch(reportError(error)));
                        break;
                }
                dispatch({
                    type: 'DELETE_SHAPE',
                    slideUuid,
                    shape,
                    redoEffect,
                    undoEffect,
                });
                await  redoEffect();
            }
        }
    }
}

export function updateTextShape(slideUuid: string, shapeUuid: string, newText: string): ThunkAction<any, State, any, UpdateTextShapeAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, UpdateTextShapeAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === slideUuid);
        if (slide) {
            const shape = slide.shapes.find(currentShape => currentShape.uuid === shapeUuid);
            if (shape && shape.type === 'TEXT') {
                const presentationId = getPresentationId(state);
                const { text: oldText, lines: oldLines, x1: oldX1, y1: oldY1, x2: oldX2, y2: oldY2, boundsType } = shape;
                const newX1 = Math.min(oldX1, oldX2);
                const newY1 = Math.min(oldY1, oldY2);
                let newX2: number;
                let newY2: number;
                let newLines: ReadonlyArray<string>;
                let newSize: number;
                if (boundsType === BoundsType.FIXED) {
                    const wrappedText = fitTextBox(newText, Math.abs(oldX2 - oldX1), Math.abs(oldY2 - oldY1));
                    newLines = wrappedText.lines;
                    newSize = wrappedText.size;
                    newX2 = Math.max(oldX1, oldX2);
                    newY2 = Math.max(oldY2, oldY1);
                } else {
                    newSize = DEFAULT_FONT_SIZE;
                    const { lines, width, height } = textNoWrap(newText, newSize);
                    newLines = lines;
                    newX2 = newX1 + width;
                    newY2 = newY1 + height;
                }

                const redoEffect = async () => await store.updateText(presentationId, slideUuid, shapeUuid, newX1, newY1, newX2, newY2, newText, boundsType)
                    .catch(error => dispatch(reportError(error)));
                const undoEffect = async () => await store.updateText(presentationId, slideUuid, shapeUuid, oldX1, oldY1, oldX2, oldY2, oldText, boundsType)
                    .catch(error => dispatch(reportError(error)));
                dispatch({
                    type: 'UPDATE_TEXT_SHAPE',
                    slideUuid,
                    shapeUuid,
                    oldText,
                    newText,
                    oldLines,
                    newLines,
                    oldX1,
                    oldY1,
                    oldX2,
                    oldY2,
                    newX1,
                    newY1,
                    newX2,
                    newY2,
                    oldSize: shape.size,
                    newSize,
                    redoEffect,
                    undoEffect,
                });
                await redoEffect();
            }
        }
    }
}

export function selectShape(shapeUuid: string | null): SelectShapeAction {
    return {
        type: 'SELECT_SHAPE',
        shapeUuid,
    }
}

export function startShapeDragging(slideUuid: string, shapeUuid: string, mouseX: number, mouseY: number): ThunkAction<any, State, any, StartShapeDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StartShapeDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === slideUuid);
        if (slide) {
            const shape = slide.shapes.find(currentShape => currentShape.uuid === shapeUuid);
            if (shape) {
                const offsetX = shape.x1 - mouseX;
                const offsetY = shape.y1 - mouseY;
                dispatch({
                    type: 'START_SHAPE_DRAGGING',
                    slideUuid,
                    shapeUuid,
                    offsetX,
                    offsetY,
                    shapeX: shape.x1,
                    shapeY: shape.y1,
                    boundsOffsetX: shape.x1 - shape.x2,
                    boundsOffsetY: shape.y1 - shape.y2,
                })
            }
        }
    };
}

export function shapeDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, ShapeDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, ShapeDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'MOVE_SHAPE') {
            const presentationId = getPresentationId(state);
            const { slideUuid, shapeUuid, startX, startY } = previousAction;
            const redoEffect = async () => await store.moveShape(presentationId, slideUuid, shapeUuid, mouseX, mouseY)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.moveShape(presentationId, slideUuid, shapeUuid, startX, startY)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'SHAPE_DRAGGING',
                slideUuid: previousAction.slideUuid,
                shapeUuid: previousAction.shapeUuid,
                endX: mouseX,
                endY: mouseY,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    }
}

export function stopShapeDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopShapeDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, StopShapeDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'MOVE_SHAPE') {
            const presentationId = getPresentationId(state);
            const { slideUuid, shapeUuid, startX, startY } = previousAction;
            const redoEffect = async () => await store.moveShape(presentationId, slideUuid, shapeUuid, mouseX, mouseY)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await store.moveShape(presentationId, slideUuid, shapeUuid, startX, startY)
                .catch(error => dispatch(reportError(error)));
            dispatch({
                type: 'STOP_SHAPE_DRAGGING',
                slideUuid: previousAction.slideUuid,
                shapeUuid: previousAction.shapeUuid,
                endX: mouseX,
                endY: mouseY,
                redoEffect,
                undoEffect,
            });
            await redoEffect();
        }
    }
}


export function startSelectorDragging(slideUuid: string, shapeUuid: string, xSelector: XSelector, ySelector: YSelector, mouseX: number, mouseY: number): ThunkAction<any, State, any, StartSelectorDraggingAction> {
    return async (dispatch: ThunkDispatch<State, any, StartSelectorDraggingAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const slides = getSlides(state);
        const slide = slides.find(currentSlide => currentSlide.uuid === slideUuid);
        if (slide) {
            const shape = slide.shapes.find(currentShape => currentShape.uuid === shapeUuid);
            if (shape) {
                const x = xSelector === XSelector.x1 ? shape.x1 : xSelector === XSelector.x2 ? shape.x2 : (shape.x1 + shape.x2) / 2;
                const y = ySelector === YSelector.y1 ? shape.y1 : ySelector === YSelector.y2 ? shape.y2 : (shape.y1 + shape.y2) / 2;
                const offsetX = x - mouseX;
                const offsetY = y - mouseY;
                dispatch({
                    type: 'START_SELECTOR_DRAGGING',
                    slideUuid,
                    shape,
                    xSelector,
                    ySelector,
                    offsetX,
                    offsetY,
                })
            }
        }
    };
}

export function selectorDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, SelectorDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, SelectorDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'RESIZE_SHAPE') {
            const { slideUuid, shapeUuid, startX1, startY1, startX2, startY2, startBoundsType } = previousAction;
            const selectorDraggingState = getSelectorDragging(state);
            if (selectorDraggingState) {
                const presentationId = getPresentationId(state);
                const { shape, xSelector, ySelector } = selectorDraggingState;
                const endX1 = xSelector === XSelector.x1 ? mouseX : shape.x1;
                const endY1 = ySelector === YSelector.y1 ? mouseY : shape.y1;
                const endX2 = xSelector === XSelector.x2 ? mouseX : shape.x2;
                const endY2 = ySelector === YSelector.y2 ? mouseY : shape.y2;
                let endPoints: ReadonlyArray<Point> = [];
                let endPath = '';
                let endLines: ReadonlyArray<string> = [];
                let endSize = 0;
                if (shape.type === 'FREEFORM') {
                    endPoints = resizePoints(shape.points, startX1, startY1, startX2, startY2, endX1, endY1, endX2, endY2);
                    endPath = convertPointsToPath(endPoints);
                } else if (shape.type === 'TEXT') {
                    const wordwrap = fitTextBox(shape.text, Math.abs(endX2 - endX1), Math.abs(endY2 - endY1));
                    endLines = wordwrap.lines;
                    endSize = wordwrap.size;
                } else {
                    endPath = createPath(shape, endX1, endY1, endX2, endY2);
                }
                const redoEffect = async () => await store.resizeShape(presentationId, slideUuid, shapeUuid, endX1, endY1, endX2, endY2)
                    .catch(error => dispatch(reportError(error)));
                const undoEffect = async () => await store.resizeShape(presentationId, slideUuid, shapeUuid, startX1, startY1, startX2, startY2, startBoundsType)
                    .catch(error => dispatch(reportError(error)));
                dispatch({
                    type: 'SELECTOR_DRAGGING',
                    slideUuid,
                    shapeUuid,
                    endX1,
                    endY1,
                    endX2,
                    endY2,
                    endPath,
                    endPoints,
                    endLines,
                    endSize,
                    redoEffect,
                    undoEffect,
                });
                await redoEffect();
            }
        }
    }
}

export function stopSelectorDragging(mouseX: number, mouseY: number): ThunkAction<any, State, any, StopSelectorDraggingAction | ReportErrorAction> {
    return async (dispatch: ThunkDispatch<State, any, StopSelectorDraggingAction | ReportErrorAction>, getState: () => State): Promise<any> => {
        const state = getState();
        const previousAction = getUndoAction(state);
        if (previousAction && previousAction.type === 'RESIZE_SHAPE') {
            const { slideUuid, shapeUuid, startX1, startY1, startX2, startY2, startBoundsType } = previousAction;
            const selectorDraggingState = getSelectorDragging(state);
            if (selectorDraggingState) {
                const presentationId = getPresentationId(state);
                const { shape, xSelector, ySelector } = selectorDraggingState;
                const endX1 = xSelector === XSelector.x1 ? mouseX : shape.x1;
                const endY1 = ySelector === YSelector.y1 ? mouseY : shape.y1;
                const endX2 = xSelector === XSelector.x2 ? mouseX : shape.x2;
                const endY2 = ySelector === YSelector.y2 ? mouseY : shape.y2;
                let endPoints: ReadonlyArray<Point> = [];
                let endPath = '';
                let endLines: ReadonlyArray<string> = [];
                let endSize = 0;
                if (shape.type === 'FREEFORM') {
                    endPoints = resizePoints(shape.points, startX1, startY1, startX2, startY2, endX1, endY1, endX2, endY2);
                    endPath = convertPointsToPath(endPoints);
                } else if (shape.type === 'TEXT') {
                    const wordwrap = fitTextBox(shape.text, Math.abs(endX2 - endX1), Math.abs(endY2 - endY1));
                    endLines = wordwrap.lines;
                    endSize = wordwrap.size;
                } else {
                    endPath = createPath(shape, endX1, endY1, endX2, endY2);
                }
                const redoEffect = async () => await store.resizeShape(presentationId, slideUuid, shapeUuid, endX1, endY1, endX2, endY2)
                    .catch(error => dispatch(reportError(error)));
                const undoEffect = async () => await store.resizeShape(presentationId, slideUuid, shapeUuid, startX1, startY1, startX2, startY2, startBoundsType)
                    .catch(error => dispatch(reportError(error)));
                dispatch({
                    type: 'STOP_SELECTOR_DRAGGING',
                    slideUuid,
                    shapeUuid,
                    endX1,
                    endY1,
                    endX2,
                    endY2,
                    endPath,
                    endPoints,
                    endLines,
                    endSize,
                    undoEffect,
                    redoEffect,
                });
                await redoEffect();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/actions/slides.ts